<script setup lang="ts">
import api from "@/api";
import {
	useDefaultStore,
	type TMainCategory,
	type TSinglePlayerGameType,
} from "@/store";
import { useToast } from "primevue/usetoast";

type TPlayerStat = {
	rank: number;
	score: number;
	username: string | null;
	isMe: boolean;
};

const props = withDefaults(
	defineProps<{
		categoryCode: TMainCategory;
		categoryName: "Music" | "Movies" | "People";
		routeName: "MoviesPage" | "MusicPage" | "PeoplePage";
	}>(),
	{},
);

const store = useDefaultStore();
const toast = useToast();

const activeTab = ref<TSinglePlayerGameType>(
	store.SINGLE_PLAYER_GAME_TYPES.CLASSIC_GAME,
);
const isLoading = ref(false);

const statsByType = ref<Record<TSinglePlayerGameType, TPlayerStat[]>>({
	CLS: [],
	BTC: [],
});

const loadedTabs = ref<Record<TSinglePlayerGameType, boolean>>({
	CLS: false,
	BTC: false,
});

const players = computed(() => statsByType.value[activeTab.value]);

function getDisplayUsername(username: string | null, rank: number) {
	return username?.trim() || `Guest`;
}

async function fetchStats(type: TSinglePlayerGameType, force = false) {
	if (isLoading.value) {
		return;
	}

	if (loadedTabs.value[type] && !force) {
		return;
	}

	try {
		isLoading.value = true;

		if (!store.userData?.usr_token) {
			await store.getUser();
		}

		const token = store.userData?.usr_token ?? "";

		const res = await api.getLeaderboard({
			token,
			type,
			category: props.categoryCode,
		});

		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.players)) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: "Unable to load stats.",
				life: 3000,
			});
			return;
		}

		statsByType.value[type] = data.players;
		loadedTabs.value[type] = true;
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to load stats.",
			life: 3000,
		});
	} finally {
		isLoading.value = false;
	}
}

async function changeTab(type: TSinglePlayerGameType) {
	if (activeTab.value === type) {
		return;
	}

	activeTab.value = type;
	await fetchStats(type);
}

function getMedal(rank: number) {
	if (rank === 1) return "🥇";
	if (rank === 2) return "🥈";
	if (rank === 3) return "🥉";
	return null;
}

onMounted(async () => {
	await fetchStats(store.SINGLE_PLAYER_GAME_TYPES.CLASSIC_GAME);
});
</script>

<template>
	<section class="stats-page">
		<div class="overlay"></div>

		<div class="page-shell">
			<div class="stats-card">
				<RouterLink
					:to="{ name: props.routeName }"
					class="category-back-link"
				>
					← Back
				</RouterLink>

				<div class="header">
					<p class="eyebrow">{{ props.categoryName }}</p>
					<h1 class="title">Leaderboard</h1>
				</div>

				<div class="tabs">
					<button
						type="button"
						class="tab-button"
						:class="{
							'is-active':
								activeTab === store.SINGLE_PLAYER_GAME_TYPES.CLASSIC_GAME,
						}"
						@click="changeTab(store.SINGLE_PLAYER_GAME_TYPES.CLASSIC_GAME)"
					>
						Classic
					</button>

					<button
						type="button"
						class="tab-button"
						:class="{
							'is-active':
								activeTab === store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK,
						}"
						@click="changeTab(store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK)"
					>
						Beat the Clock
					</button>
				</div>
				<div
					v-if="isLoading"
					class="leaderboard-loader"
				>
					<div class="loader-spinner"></div>
				</div>

				<div
					v-else-if="!players.length"
					class="state-box"
				>
					No stats yet.
				</div>

				<div
					v-else
					class="leaderboard"
				>
					<div class="leaderboard-head">
						<span>Rank</span>
						<span>Player</span>
						<span>Score</span>
					</div>

					<div class="leaderboard-list">
						<div
							v-for="player in players"
							:key="`${activeTab}-${player.rank}-${player.score}-${player.username}`"
							class="leaderboard-row"
							:class="{ 'is-me': player.isMe }"
						>
							<span class="rank">#{{ player.rank }}</span>

							<span class="player">
								<span
									v-if="getMedal(player.rank)"
									class="player-medal"
								>
									{{ getMedal(player.rank) }}
								</span>

								<span class="player-name">
									{{ getDisplayUsername(player.username, player.rank) }}
								</span>
							</span>

							<strong class="score">{{ player.score }}</strong>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.stats-page {
	position: relative;
	min-height: 100svh;
	overflow: hidden;
	background:
		radial-gradient(circle at center, #2a2a2a 0%, #171717 46%, #070707 100%),
		#101010;
	// background-image: url("https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80");
	// background-size: cover;
	// background-position: center;
	// background-repeat: no-repeat;
	// isolation: isolate;
}

.overlay {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0.04) 0%,
		rgba(0, 0, 0, 0.08) 42%,
		rgba(0, 0, 0, 0.42) 100%
		);
	z-index: 0;
}

.page-shell {
	position: relative;
	z-index: 1;
	min-height: 100dvh;
	padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
	display: flex;
	align-items: flex-start;
	justify-content: center;
	overflow: hidden;
}

.stats-card {
	width: 100%;
	max-width: 880px;
	max-height: calc(100dvh - 40px - env(safe-area-inset-bottom));
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: contain;
	padding: 20px 16px;
	border-radius: 24px;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	scrollbar-width: thin;
	scrollbar-color: rgba(255, 255, 255, 0.16) transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.16);
		border-radius: 999px;
	}
}

.back-link {
	align-self: flex-start;
	margin-bottom: 14px;
	padding: 8px 12px;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.1);
	color: rgba(255, 255, 255, 0.82);
	font-size: 13px;
	font-weight: 700;
	text-decoration: none;
	backdrop-filter: blur(8px);
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.14);
		border-color: rgba(255, 255, 255, 0.16);
		color: $text-color-white;
	}
}

.header {
	text-align: center;
	flex-shrink: 0;
}

.eyebrow {
	margin: 0 0 8px;
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 0.16em;
	text-transform: uppercase;
	color: $text-color-grey;
}

.title {
	margin: 0;
	font-size: clamp(32px, 8vw, 56px);
	line-height: 0.95;
	font-weight: 900;
	letter-spacing: -0.03em;
	color: $text-color-white;
	text-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.tabs {
	margin-top: 24px;
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	flex-shrink: 0;
}

.tab-button {
	border: 0;
	outline: 0;
	padding: 12px 14px;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.82);
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition:
		background 0.2s ease,
		transform 0.2s ease,
		color 0.2s ease;

	&.is-active {
		background: rgba(255, 255, 255, 0.18);
		color: $text-color-white;
	}

	&:hover {
		background: rgba(255, 255, 255, 0.14);
	}
}

.state-box {
	margin-top: 18px;
	padding: 18px;
	border-radius: 18px;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.8);
	text-align: center;
	font-size: 14px;
	font-weight: 600;
	flex-shrink: 0;
}

.leaderboard {
	margin-top: 18px;
	display: flex;
	flex-direction: column;
}

.leaderboard-list {
	margin-top: 10px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding-right: 2px;
}

.leaderboard-head {
	display: grid;
	grid-template-columns: 36px minmax(0, 1fr) 34px;
	align-items: center;
	gap: 8px;
	padding: 14px 16px;
	border-radius: 18px;
	background: rgba(255, 255, 255, 0.06);
	color: rgba(255, 255, 255, 0.58);
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	flex-shrink: 0;
}

.leaderboard-row {
	display: grid;
	grid-template-columns: 36px minmax(0, 1fr) 34px;
	align-items: center;
	gap: 8px;
	padding: 14px 16px;
	border-radius: 18px;
	background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: $text-color-white;

	&.is-me {
		background: rgba(255, 255, 255, 0.2);
		border-color: rgba(255, 255, 255, 0.24);
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
	}
}

.rank {
	font-weight: 800;
	color: rgba(255, 255, 255, 0.85);
}

.player {
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	font-weight: 700;
}

.player-medal {
	flex: 0 0 auto;
	font-size: 14px;
	line-height: 1;
}

.player-name {
	min-width: 0;
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.score {
	text-align: right;
	font-size: 20px;
	line-height: 1;
	color: $text-color-white;
}

.leaderboard-loader {
	margin-top: 18px;
	flex: 1;
	min-height: 180px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 18px;
	background: rgba(255, 255, 255, 0.08);
}

.loader-spinner {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	border: 4px solid rgba(255, 255, 255, 0.12);
	border-top-color: rgba(255, 255, 255, 0.88);
	animation: stats-spin 0.85s linear infinite;
}

@keyframes stats-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (min-width: 768px) {
	.page-shell {
		padding: 32px 20px calc(32px + env(safe-area-inset-bottom));
	}

	.stats-card {
		padding: 28px 24px;
		max-height: calc(100dvh - 64px - env(safe-area-inset-bottom));
	}

	.back-link {
		margin-bottom: 0;
		font-size: 14px;
	}

	.tab-button {
		font-size: 15px;
	}

	.leaderboard-head,
	.leaderboard-row {
		grid-template-columns: 90px minmax(0, 1fr) 90px;
		gap: 12px;
		padding: 16px 18px;
	}

	.player {
		gap: 8px;
	}

	.player-medal {
		font-size: 16px;
	}
}
</style>
